import {useState} from 'react'
import {date, formatDateRange} from '@eitje/utils'
import {
	DatePickerProvider,
	DatePickerControl,
	DatePickerDayView,
	DatePickerMonthView,
	DatePickerPickerRoot,
	DatePickerRoot,
	DatePickerTrigger,
	DatePickerYearView,
} from '~/components/date_based_pickers/components'
import {EitjeButton} from '~/components/eitje_button'
import {Text} from '~/components/text'

/**
 * @typedef {Object} DateRangePickerProps
 * @property {(string | import('moment').Moment)[] | null} [value]
 * Controlled range. Strings are normalized to the date values expected by Ark UI; null or an empty array clears the picker.
 * @property {(value: import('moment').Moment[]) => void} onChange
 * Called with the completed range converted to Moments. Closing after selecting only a start date uses that date as both range boundaries.
 * @property {string | import('moment').Moment} [defaultPickerValue] Initial date focused when the picker opens.
 * @property {(datePicker: import('~/components/date_based_pickers/helpers').DatePickerContextValues) => import('react').ReactNode} [renderTrigger]
 * Renders a custom non-editable trigger with the adapted date-picker context. The default trigger displays the formatted range.
 * @property {(datePicker: import('~/components/date_based_pickers/helpers').DatePickerContextValues) => import('react').ReactNode} [renderExtraFooter]
 * Renders additional content below the picker views with the adapted date-picker context.
 * @property {string | import('moment').Moment} [minDate] Earliest selectable date, inclusively.
 * @property {string | import('moment').Moment} [maxDate] Latest selectable date, inclusively.
 * @property {number} [maxSelectedDates] Maximum number of calendar dates in the range, including the start and end dates.
 * @property {number} [numOfMonths] Number of months displayed at once. Defaults to one.
 * @property {string} [placeholder] Placeholder shown by the default trigger when no dates are selected.
 * @property {boolean} [disabled] Prevents interaction with the picker.
 * @property {boolean} [readOnly] Prevents value changes while retaining the selected range.
 * @property {(open: boolean) => void} [onOpenChange] Reports whether the picker is open.
 * @property {(date: import('moment').Moment) => boolean} [disabledDateFn] Predicate for whether a date should be disabled.
 */

/** @param {DateRangePickerProps} props */
export const DateRangePicker = ({
	value,
	defaultPickerValue,
	placeholder,
	minDate,
	maxDate,
	maxSelectedDates,
	numOfMonths = 1,
	disabled,
	readOnly,
	renderTrigger,
	renderExtraFooter,
	onOpenChange,
	onChange,
	disabledDateFn,
}) => {
	const [draftRange, setDraftRange] = useState([])

	// Only parse value if both start and end dates are present
	const selectedRange = value && value.filter(Boolean).length === 2 ? value.map(selectedDate => date(selectedDate)) : []
	const valueOverride = draftRange.length > 0 ? draftRange : selectedRange

	const handleChange = selectedDates => {
		if (selectedDates.length === 1) {
			setDraftRange(selectedDates)
			return
		}

		setDraftRange([])
		onChange(selectedDates)
	}
	const handleOpenChange = isOpen => {
		if (!isOpen) {
			const [draftStartDate, draftEndDate] = draftRange

			// If only the start date is drafted upon closing,
			// we assume the user just wants to select 1 day as the range
			if (draftStartDate && !draftEndDate) {
				handleChange([draftStartDate, draftStartDate])
			}

			setDraftRange([])
		}

		onOpenChange?.(isOpen)
	}

	const isDateDisabled = date => {
		if (disabledDateFn?.(date)) return true
		if (!maxSelectedDates || valueOverride.length !== 1) return false

		const startDate = valueOverride[0]
		return Math.abs(date.diff(startDate, 'days')) >= maxSelectedDates
	}

	let triggerElement = (
		<DatePickerProvider>
			{datePicker => (
				<DatePickerTrigger>
					<EitjeButton dropdown>
						<Text>{datePicker.value.length === 0 && placeholder ? placeholder : formatDateRange(datePicker.value)}</Text>
					</EitjeButton>
				</DatePickerTrigger>
			)}
		</DatePickerProvider>
	)
	if (renderTrigger)
		triggerElement = (
			<DatePickerProvider>{datePicker => <DatePickerTrigger>{renderTrigger(datePicker)}</DatePickerTrigger>}</DatePickerProvider>
		)

	const defaultFocusedValue = defaultPickerValue ? date(defaultPickerValue) : undefined
	const minimumDate = minDate ? date(minDate) : undefined
	const maximumDate = maxDate ? date(maxDate) : undefined

	return (
		<DatePickerRoot
			value={valueOverride}
			mode="range"
			numOfMonths={numOfMonths}
			defaultFocusedValue={defaultFocusedValue}
			minDate={minimumDate}
			maxDate={maximumDate}
			disabled={disabled}
			readOnly={readOnly}
			onOpenChange={handleOpenChange}
			onChange={handleChange}
			disabledDateFn={isDateDisabled}
		>
			<DatePickerControl>{triggerElement}</DatePickerControl>
			<DatePickerPickerRoot renderExtraFooter={renderExtraFooter}>
				<DatePickerDayView />
				<DatePickerMonthView />
				<DatePickerYearView />
			</DatePickerPickerRoot>
		</DatePickerRoot>
	)
}
